import { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/pages/AppLayout";
import {
  ArrowLeft, HardHat, User, ClipboardCheck, ClipboardList, Wrench, Truck,
  AlertTriangle, ShieldCheck, DollarSign,
} from "lucide-react";

const aud = (v) => `$${Number(v || 0).toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="card p-4 border-t-4" style={{ borderTopColor: accent }} data-testid={`profile-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon className="w-4 h-4 mb-2" style={{ color: accent }} />
      <p className="font-mono text-3xl" style={{ color: accent }}>{value}</p>
      <p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] mt-1">{label}</p>
    </div>
  );
}

export default function MemberProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { fleet } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (fleet && !fleet.is_owner) return;
    api.get(`/fleet/members/${id}/profile`).then(({ data }) => setD(data)).catch((e) => setErr(e.response?.data?.detail || "Could not load profile"));
  }, [id, fleet]);

  if (fleet && !fleet.is_owner) return <Navigate to="/team" replace />;

  if (err) return <div className="p-5 md:p-10" data-testid="member-profile-page"><p className="font-body text-[#FF3B30]">{err}</p></div>;
  if (!d) return <div className="p-5 md:p-10" data-testid="member-profile-page"><p className="font-head text-[#FFB800] uppercase tracking-widest">Loading…</p></div>;

  const { member, stats } = d;
  const prioLabel = { "1day": "Within 1 Day", "3day": "2–3 Days", "5day": "5+ Days" };

  return (
    <div className="p-5 md:p-10 pb-24 max-w-4xl" data-testid="member-profile-page">
      <button onClick={() => nav("/team")} className="flex items-center gap-2 text-[#94A3B8] font-body text-sm mb-4" data-testid="profile-back"><ArrowLeft className="w-4 h-4" /> Team</button>

      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 bg-[#0D0E11] border border-white/10 flex items-center justify-center">
          {member.role === "manager" ? <HardHat className="w-7 h-7 text-[#FFB800]" /> : <User className="w-7 h-7 text-[#94A3B8]" />}
        </div>
        <div>
          <h1 className="font-head font-900 uppercase tracking-tight text-3xl">{member.name}</h1>
          <p className="font-mono text-xs text-[#94A3B8]">{member.email} · {member.role} · joined {fmtDate(member.created_at)}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <Stat icon={ClipboardList} label="Open Jobs" value={stats.work_orders_open} accent="#FF3B30" />
        <Stat icon={ClipboardCheck} label="Jobs Done" value={stats.work_orders_done} accent="#22C55E" />
        <Stat icon={ShieldCheck} label="Pre-Starts" value={stats.prestarts} accent="#FFB800" />
        <Stat icon={Wrench} label="Services" value={stats.services} accent="#3B82F6" />
        <Stat icon={Truck} label="Machines" value={stats.machines_worked} accent="#A855F7" />
      </div>

      {d.machines_worked.length > 0 && (
        <div className="card p-5 mb-4" data-testid="profile-machines">
          <h2 className="font-head font-700 text-lg uppercase tracking-wide mb-3">Machines Worked On</h2>
          <div className="flex flex-wrap gap-2">
            {d.machines_worked.map((m, i) => <span key={i} className="font-mono text-xs bg-white/5 border border-white/10 px-2.5 py-1">{m}</span>)}
          </div>
        </div>
      )}

      <div className="card mb-4" data-testid="profile-workorders">
        <div className="px-5 py-4 border-b border-white/10"><h2 className="font-head font-700 text-lg uppercase tracking-wide">Assigned Work Orders ({d.work_orders.length})</h2></div>
        {d.work_orders.length === 0 ? <p className="p-5 font-body text-sm text-[#94A3B8]">No work orders assigned.</p> : (
          <div className="divide-y divide-white/10">
            {d.work_orders.map((w) => (
              <div key={w.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-body font-700 truncate">{w.title}</p>
                  <p className="font-mono text-[11px] text-[#94A3B8]">{w.machine || "—"} · {prioLabel[w.priority] || w.priority}</p>
                </div>
                <span className={`badge ${w.status === "done" ? "badge-healthy" : "badge-overdue"} shrink-0`}>{w.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card mb-4" data-testid="profile-prestarts">
        <div className="px-5 py-4 border-b border-white/10"><h2 className="font-head font-700 text-lg uppercase tracking-wide">Pre-Start Checks ({stats.prestarts})</h2></div>
        {d.prestarts.length === 0 ? <p className="p-5 font-body text-sm text-[#94A3B8]">No pre-starts logged.</p> : (
          <div className="divide-y divide-white/10">
            {d.prestarts.map((p) => (
              <div key={p.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  {p.overall === "fail" ? <AlertTriangle className="w-4 h-4 text-[#FF3B30] shrink-0" /> : <ShieldCheck className="w-4 h-4 text-[#22C55E] shrink-0" />}
                  <div className="min-w-0">
                    <p className="font-body truncate">{p.machine}</p>
                    {p.overall === "fail" && p.failed_items.length > 0 && <p className="font-mono text-[11px] text-[#FF3B30]">{p.failed_items.join(", ")}</p>}
                  </div>
                </div>
                <span className="font-mono text-[11px] text-[#94A3B8] shrink-0">{fmtDate(p.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card" data-testid="profile-services">
        <div className="px-5 py-4 border-b border-white/10"><h2 className="font-head font-700 text-lg uppercase tracking-wide">Services Logged ({stats.services})</h2></div>
        {d.services.length === 0 ? <p className="p-5 font-body text-sm text-[#94A3B8]">No services logged.</p> : (
          <div className="divide-y divide-white/10">
            {d.services.map((s) => (
              <div key={s.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-body font-700 truncate">{s.service_type}</p>
                  <p className="font-mono text-[11px] text-[#94A3B8]">{s.machine}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono text-sm text-[#22C55E] flex items-center gap-1 justify-end"><DollarSign className="w-3 h-3" />{aud(s.cost)}</p>
                  <p className="font-mono text-[11px] text-[#94A3B8]">{fmtDate(s.service_date)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
